import { Injectable } from '@angular/core';
import { IUserServiceCardCalendar } from '@app/core/components/datetime-picker/models';
import { DestroyableComponent } from '@models/destroyable.component';
import {
  Collection,
  EUserServiceType,
  IBillingCard,
  IContentBlock,
  IFindUserServicesFilter,
  ITeachingSubject,
  Meeting,
  User,
  UserService,
} from 'lingo2-models';
import { BehaviorSubject } from 'rxjs';

export type UserServiceCheckoutBlockType = IContentBlock<UserService, IFindUserServicesFilter>;

export type PaymentCheckoutContentType =
  | 'meeting'
  | 'user-service'
  | 'collection'
  | 'tariff-plan'
  | 'paywall'
  | 'credits';

export type PaymentCheckoutWizardCheckoutStepType = 'teacher' | 'detail' | 'datetime' | 'payment' | 'finally';

export interface ITeachingSubjectItem {
  value: string | number;
  title: string;
  subject: ITeachingSubject;
}

interface IContentInitConfiguration {
  teacher: User;
  content: Meeting | UserService | Collection;
  moreContent: Meeting[] | UserService[] | Collection[];
  cards: IBillingCard[];
  time: Date;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentCheckoutWizardService extends DestroyableComponent {
  public set time(value: Date) {
    this._time = value;
  }
  public get time() {
    return this._time;
  }
  public get step() {
    return this._step;
  }
  public set step(step) {
    this._step = step;
  }
  public set content(value: Meeting | UserService | Collection) {
    this._content = value;
    this._state.next(this._step);
  }
  public get content(): Meeting | UserService | Collection {
    return this._content;
  }
  public set services(value: Meeting[] | UserService[] | Collection[]) {
    this._moreContent = value;
    this._state.next(this._step);
  }
  public get services(): Meeting[] | UserService[] | Collection[] {
    return this._moreContent;
  }
  public set teacher(value: User) {
    this._teacher = value;
    this._state.next(this._step);
  }
  public get teacher(): User {
    return this._teacher;
  }

  private _billingCard: IBillingCard;
  private _userCards: IBillingCard[] = [];

  public calendar: IUserServiceCardCalendar = {
    loading: false,
    loaded: false,
    events: [],
    filter: {
      date_from: null,
      date_to: null,
    },
    date: null,
    time: [],
  };

  public steps: PaymentCheckoutWizardCheckoutStepType[];

  public _state = new BehaviorSubject<PaymentCheckoutWizardCheckoutStepType>(null);
  public state$ = this._state.asObservable();

  public passed_steps: Record<PaymentCheckoutWizardCheckoutStepType, boolean> = {
    teacher: false,
    detail: false,
    datetime: false,
    payment: false,
    finally: false,
  };
  public done = false;
  public fail = false;

  private _time: Date;
  private _teacher: User;
  private _step: PaymentCheckoutWizardCheckoutStepType = 'detail';
  private _content: Meeting | UserService | Collection;
  private _moreContent: Meeting[] | UserService[] | Collection[];

  public setCalendarLoading(isLoading = true) {
    if (!isLoading && this.calendar.loading) {
      this.calendar.loaded = true;
    }

    this.calendar.loading = isLoading;
  }

  public setTimes(times: Date[]) {
    this.calendar.time = times;
  }

  public addTime(time: Date) {
    this.calendar.time.push(time);
  }

  public resetCalendar() {
    this.calendar.time = [];
  }

  public setBillingCard(billingCard: IBillingCard) {
    this._billingCard = billingCard;
  }

  public get billingCard() {
    return this._billingCard;
  }

  public setUserCards(userCards: IBillingCard[]) {
    this._userCards = userCards;
  }

  public get userCards() {
    return this._userCards;
  }

  public reset() {
    this._step = this.steps[0];
    this.passed_steps = {
      teacher: true,
      detail: true,
      datetime: true,
      payment: true,
      finally: true,
    };

    this.steps.forEach((step) => (this.passed_steps[step] = false));
    this.done = false;
    this.fail = false;
    this._state.next(this._step);
  }

  clearData() {
    this._teacher = null;
    this._moreContent = null;
    this._userCards = null;
    this._content = null;
    this._time = null;
  }

  public insertStep(step: PaymentCheckoutWizardCheckoutStepType) {
    this.passed_steps[step] = false;
    this.steps.unshift(step);
  }

  public goNextStep(currentStep?: PaymentCheckoutWizardCheckoutStepType) {
    const _step = currentStep || this._step;
    switch (_step) {
      case 'teacher':
        if (!this._teacher) {
          return;
        }
        this.passed_steps.teacher = true;
        if (this._content) {
          this.goNextStep('detail');
          return;
        }
        this.goStep('detail');
        break;

      case 'detail':
        if (!this._content) {
          return;
        }
        this.passed_steps.detail = true;
        if (this.content instanceof UserService) {
          this.goStep('datetime');
        } else {
          this.goStep('payment');
        }
        break;

      case 'datetime':
        if (
          !this.calendar.time.length &&
          !(this.content.type === EUserServiceType.mini || this.content.type === EUserServiceType.course)
        ) {
          return;
        }
        this.passed_steps.datetime = true;
        this.goStep('payment');
        break;
    }
    this._state.next(this._step);
  }

  public goStep(step?: PaymentCheckoutWizardCheckoutStepType) {
    if (this.canGoTo(step)) {
      this._step = step;
      this._state.next(step);
    }
  }

  public goDone() {
    this.done = true;
    this._state.next(this._step);
  }

  public goFail() {
    this.fail = true;
    this.done = true;
    this._state.next(this._step);
  }

  public isPassedStep(step: PaymentCheckoutWizardCheckoutStepType): boolean {
    return this.passed_steps[step] || false;
  }

  public isCurrentStep(step: PaymentCheckoutWizardCheckoutStepType): boolean {
    return !this.done && this._step === step;
  }

  public isReadyForReserve() {
    return this.steps.length === 1 && this.steps[0] === 'payment';
  }

  public init(config?: Partial<IContentInitConfiguration>, firstTime = false) {
    this._teacher = config?.teacher || this._teacher;
    this._moreContent = config?.moreContent || this._moreContent;
    this._userCards = config?.cards || this._userCards;
    this._content = config?.content || this._content;
    this._time = config?.time || this._time;

    if (this._content instanceof UserService) {
      if (this._content?.school) {
        if (this._content?.teachers?.length > 1) {
          this._teacher = null;
        } else {
          if (this._content.teachers.length === 0) {
            this._teacher = this._content.author;
          } else {
            this._teacher = this._content.teachers[0];
          }
        }
      }
    }

    if (this._content instanceof Meeting) {
      this.generateSteps('meeting', firstTime);
    } else if (this._content instanceof UserService) {
      this.generateSteps('user-service', firstTime);
    } else if (this._content instanceof Collection) {
      this.generateSteps('collection', firstTime);
    } else {
      this.generateSteps('user-service', firstTime);
    }
  }

  public get possibleTeachers(): User[] {
    if (this._content instanceof UserService) {
      if (this._content?.teachers?.length > 1) {
        return this._content.teachers;
      }
    }

    if (this._content?.author) {
      return [this._content?.author];
    }

    return [];
  }

  public contentType(content: Meeting | UserService | Collection) {
    if (content instanceof Meeting) {
      return 'meeting';
    } else if (content instanceof UserService) {
      return 'user-service';
    } else if (content instanceof Collection) {
      return 'collection';
    }
  }

  private canGoTo(step: PaymentCheckoutWizardCheckoutStepType) {
    if (this.isPassedStep(step)) {
      return false;
    }

    let stepNumber = 0;
    while (this.steps[stepNumber] !== step) {
      const isStepPassed = this.passed_steps[this.steps[stepNumber]];
      if (!isStepPassed) {
        return false;
      }
      stepNumber += 1;
    }

    return true;
  }

  generateSteps(contentType: PaymentCheckoutContentType, firstTime) {
    if (!firstTime) {
      return;
    }
    switch (contentType) {
      case 'meeting':
        this.steps = ['payment'];
        break;
      case 'user-service':
        this.steps = ['payment'];
        if (!this._time) {
          this.insertStep('datetime');
        }
        if (!this._content) {
          this.insertStep('detail');
        }
        if (!this._teacher) {
          this.insertStep('teacher');
        }
        break;
      case 'collection':
        this.steps = ['payment'];
        this.insertStep('detail');
        break;
    }
    this.reset();
  }
}
